import type { Component, ScrubAnimationOptions } from '@wix/thunderbolt-becky-types'
import type { FeatureRefs } from '../cssFeatures.types'
import { createCssCompNode } from '../cssCompNode'
import type { VariantRelationViewItem } from '@wix/thunderbolt-catharsis'
import { envRefs } from '@wix/thunderbolt-catharsis'
import { toBreakpointVariantsMap } from '../../utils/toBreakpointVariantsMap'
import { toCompVariants } from '../../utils/toCompVariants'
import { isVariantRelation } from '../../utils/variantsUtils'

function getMobileVariation(
	values: Array<ScrubAnimationOptions | VariantRelationViewItem<ScrubAnimationOptions>>
): ScrubAnimationOptions | null {
	for (const value of values) {
		if (isVariantRelation(value) && value.variants.find((variant) => variant.type === 'Mobile')) {
			return value.to
		}
	}

	return values[0] as ScrubAnimationOptions
}

const BG_SCROLL_EFFECTS = [
	'BgCloseUp',
	'BgFade',
	'BgFadeBack',
	'BgFake3D',
	'BgPan',
	'BgParallax',
	'BgPullBack',
	'BgReveal',
	'BgRotate',
	'BgSkew',
	'BgZoom',
	'ImageParallax',
]

export const motion = createCssCompNode('motion', 'motion', {
	getDependencies: (component: Component, refs: FeatureRefs<'motion'>) =>
		component.effectsQuery
			? {
				effectsData: refs.effectsQuery(component.effectsQuery),
				isMobileView: envRefs.isMobileView,
				hasResponsiveLayout: refs.hasResponsiveLayout(component),
				componentViewMode: envRefs.componentViewMode,
			}
			: null,

	toViewItem: (component, deps) => {
		if (!deps) {
			return null
		}

		const { effectsData, isMobileView, componentViewMode } = deps
		const effects = effectsData?.values

		if (!effects?.length) {
			return null
		}

		return effects
			.filter(
				(effect) =>
					effect.type === 'ScrubAnimation' &&
					effect.value.values.some((value) => {
						const namedEffect =
							(value as ScrubAnimationOptions).namedEffect ||
							(value as VariantRelationViewItem<ScrubAnimationOptions>).to?.namedEffect

						if (namedEffect) {
							return BG_SCROLL_EFFECTS.includes(namedEffect.type)
						}

						return false
					})
			)
			.map((effect) => ({
				componentViewMode,
				variants: toCompVariants(effect.value.values),
				data: deps.hasResponsiveLayout
					? toBreakpointVariantsMap(
						effect.value.values as Array<
								ScrubAnimationOptions | VariantRelationViewItem<ScrubAnimationOptions>
						>,
						(x) => x
					)
					: {
						default: {
							'': isMobileView
								? getMobileVariation(
									effect.value.values as Array<
												ScrubAnimationOptions | VariantRelationViewItem<ScrubAnimationOptions>
									>
								)
								: (effect.value.values[0] as ScrubAnimationOptions),
						},
					},
			}))
	},
})
